import { Fragment, createElement, type ReactNode } from 'react';
import { getLinkItemAt } from '../links/link-item';
import { resolveLinkAttributes } from '../links/resolve-link-attributes';
import type { AnchorAttributes } from '../links/resolve-link-attributes';
import { getSpanStyleItemAt } from '../span-styles/span-style-item';
import { resolveSpanStyleAttributes } from '../span-styles/resolve-span-style-attributes';
import type { SpanStyleAttributes } from '../span-styles/resolve-span-style-attributes';
import { parseInlineMarkers } from './parse-inline-markers';
import type { InlineMarks } from './parse-inline-markers';

/**
 * Which element each mark renders as, outermost first.
 *
 * `s` rather than `del`: struck CMS copy is text that no longer applies (an old
 * price), not an edit to the document.
 */
const MARK_TAGS: ReadonlyArray<readonly [keyof InlineMarks, 'strong' | 'em' | 's']> = [
  ['bold', 'strong'],
  ['italic', 'em'],
  ['strike', 's'],
];

const hasMark = (marks: InlineMarks): boolean => marks.bold || marks.italic || marks.strike;

export type PreparedTextRun = { kind: 'text'; words: string };

export type PreparedSpanRun = {
  kind: 'span';
  words: string;
  marks: InlineMarks;
  anchor: AnchorAttributes | undefined;
  spanStyle: SpanStyleAttributes | undefined;
};

export type PreparedBreakRun = { kind: 'break' };

/**
 * One run after parsing, filling, and resolving link / styling entries. Shared
 * by the full renderer and the head/tail split so both nest elements the same
 * way.
 */
export type PreparedMarkupRun = PreparedTextRun | PreparedSpanRun | PreparedBreakRun;

/**
 * The anchor stays the innermost element, so marks wrap it rather than the
 * other way round — `<strong><a>…</a></strong>`. Keeps the one rule from
 * `INNER_LINK_TAGS_ARCHITECTURE.md` true no matter what else a span carries.
 *
 * A styling entry becomes the outermost element, outside the marks: the marks
 * say what these words *are*, and the styling says how this run of words looks,
 * which is the container of the other two. It also means an author who sets a
 * colour on bold words does not have to know which element the colour lands on.
 */
const renderSpan = (
  words: string,
  marks: InlineMarks,
  anchor: AnchorAttributes | undefined,
  spanStyle: SpanStyleAttributes | undefined
): ReactNode => {
  let node: ReactNode = anchor === undefined ? words : createElement('a', anchor, words);

  for (let position = MARK_TAGS.length - 1; position >= 0; position -= 1) {
    const [mark, tag] = MARK_TAGS[position];
    if (marks[mark]) {
      node = createElement(tag, null, node);
    }
  }

  return spanStyle === undefined ? node : createElement('span', spanStyle, node);
};

/**
 * Parses markers, fills words, and resolves anchors and span styles into a
 * list the renderers can share. Empty filled words stay in the list so run
 * indexes stay stable for splitting; they contribute nothing when rendered.
 */
export function prepareInlineMarkupRuns(
  text: string,
  links: unknown,
  spanStyles?: unknown,
  fillText?: (words: string) => string
): PreparedMarkupRun[] {
  const runs = parseInlineMarkers(text);

  return runs.map((run) => {
    if (run.kind === 'break') {
      return { kind: 'break' };
    }

    const words = fillText === undefined ? run.text : fillText(run.text);

    if (run.kind === 'text') {
      return { kind: 'text', words };
    }

    return {
      kind: 'span',
      words,
      marks: run.marks,
      anchor:
        run.index !== undefined
          ? resolveLinkAttributes(getLinkItemAt(links, run.index))
          : undefined,
      spanStyle:
        run.styleIndex !== undefined
          ? resolveSpanStyleAttributes(getSpanStyleItemAt(spanStyles, run.styleIndex))
          : undefined,
    };
  });
}

/**
 * Turns prepared runs into the same React output `renderInlineMarkup` has
 * always produced: a plain string when no element is needed, otherwise a run
 * of text nodes, marks, anchors, styling spans, and breaks.
 */
export function renderPreparedMarkupRuns(runs: PreparedMarkupRun[]): ReactNode {
  const needsElement = runs.some(
    (run) =>
      run.kind === 'break' ||
      (run.kind === 'span' &&
        run.words !== '' &&
        (run.anchor !== undefined || run.spanStyle !== undefined || hasMark(run.marks)))
  );

  if (!needsElement) {
    return runs.map((run) => (run.kind === 'break' ? '' : run.words)).join('');
  }

  return runs.map((run, position) => {
    if (run.kind === 'text') {
      return run.words;
    }
    if (run.kind === 'break') {
      return <br key={position} />;
    }
    if (run.words === '') {
      return null;
    }
    return (
      <Fragment key={position}>
        {renderSpan(run.words, run.marks, run.anchor, run.spanStyle)}
      </Fragment>
    );
  });
}
